import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep, merge } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as yup from 'yup';
import { ContentWithFooter } from '../../../../../../components/ui/content-with-footer';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { sleep } from '../../../../../../util/async';
import { useIsMounted } from '../../../../../../util/hooks';
import { TopTabs } from '../../../../shared/component/tabs/top-tabs';
import { ApplicationDefaultParameterSetFormikFormValues } from '../../../../shared/hook/application-default-parameter-set.hooks';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { ApplicationDefaultParameterSetBaseFormProps } from '../../../../shared/model/flow.types';
import { getParameterValueFromForm } from '../../../../shared/util/flow-parameter.util';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { AssistedCutProcessSettingsForm } from '../flow-item/assisted-cut-process-settings-form';
import { CutProcessSettingsForm } from '../flow-item/cut-process-settings-form';
import { CutApplicationSettingsTags } from './cut-application-settings-tags';

export type CutParameterSetBaseFormValidationBuilder = (
  dataSchema: yup.AnySchema<unknown, unknown, unknown> | undefined
) => yup.AnyObjectSchema;

enum CutApplicationSettingsTab {
  ASSISTED = 'assisted',
  MANUAL = 'manual',
}

interface CutApplicationSetBaseFormTabbedProps<T>
  extends ApplicationDefaultParameterSetBaseFormProps<T> {
  onSubmit: (form: T, recommendationId?: string) => Promise<void>;
}

function CutApplicationSettingsBaseFormTabbed<
  T extends Pick<
    ApplicationDefaultParameterSetFormikFormValues,
    'tags' | 'data'
  >,
>(props: CutApplicationSetBaseFormTabbedProps<T>): ReactElement {
  const {
    paramsReadOnly,
    tagsReadOnly,
    excludeTags,
    system,
    footer,
    formValues,
    onSubmit,
    preForm,
    postForm,
    buildValidationSchema,
  } = props;

  const { flow } = useFlowServices();
  const formikRef = useRef<FormikProps<T> | null>(null);
  const [recommendationId, setRecommendationId] = useState<string | undefined>(
    flow.recommendation?.id
  );
  const [recommendationLoading, setRecommendationLoading] = useState(false);

  const setInnerRef = useCallback((instance: FormikProps<T> | null) => {
    formikRef.current = instance;
  }, []);

  const [dataSchema, setDataSchema] = useState<yup.AnySchema>();

  const validationSchema = useMemo(() => {
    return buildValidationSchema(dataSchema);
  }, [buildValidationSchema, dataSchema]);

  const setParameterValidationSchema = useCallback((schema?: yup.AnySchema) => {
    requestAnimationFrame(() => {
      setDataSchema(schema);
      formikRef.current?.validateForm();
    });
  }, []);

  const parameterOptions = merge(
    cloneDeep(BeaconCuttingData.DefaultCutParameterMetadataFromFormOptions),
    {
      forceExcludeRefs: [
        ...(BeaconCuttingData.DefaultCutParameterMetadataFromFormOptions
          .forceExcludeRefs ?? []),
      ],
    } as Partial<BeaconCuttingData.GetParameterMetadataFromFormOptions>
  );

  const initialTab =
    flow.recommendation === undefined && flow.defaultParameterSet !== undefined
      ? CutApplicationSettingsTab.MANUAL
      : CutApplicationSettingsTab.ASSISTED;
  const [assistedSelected, setAssistedSelected] = useState<boolean>(
    initialTab === CutApplicationSettingsTab.ASSISTED
  );

  const tabs = useMemo(() => {
    return [
      {
        id: CutApplicationSettingsTab.ASSISTED,
        label: getLocalizedText('common:assisted') ?? 'Assisted',
        component: (
          <AssistedCutProcessSettingsForm
            system={system}
            readOnly={true}
            formikValuePath="data"
            setValidationSchema={setParameterValidationSchema}
            parameterListPrefix="data.parameters"
            parameterOptions={parameterOptions}
            onRecommendSettings={setRecommendationId}
            onRecommendSettingsLoading={setRecommendationLoading}
            preventRecommendation={!assistedSelected}
          />
        ),
      },
      {
        id: CutApplicationSettingsTab.MANUAL,
        label: getLocalizedText('common:manual') ?? 'Manual',
        component: (
          <>
            <CutProcessSettingsForm
              system={system}
              readOnly={paramsReadOnly}
              formikValuePath="data"
              setValidationSchema={setParameterValidationSchema}
              parameterListPrefix="data.parameters"
              parameterOptions={parameterOptions}
            />
          </>
        ),
      },
    ];
  }, [
    assistedSelected,
    parameterOptions,
    paramsReadOnly,
    setParameterValidationSchema,
    system,
  ]);

  const [assistedTabData, setAssistedTabData] =
    useState<
      Pick<ApplicationDefaultParameterSetFormikFormValues, 'data' | 'tags'>
    >(formValues);
  const [manualTabData, setManualTabData] =
    useState<
      Pick<ApplicationDefaultParameterSetFormikFormValues, 'data' | 'tags'>
    >(formValues);

  const topTabs = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValues: (values: any, validate?: boolean) => void,
      validateForm: () => void
    ) => {
      return (
        <TopTabs
          tabs={tabs}
          initialTab={initialTab}
          onTabChange={(id) => {
            if (id === CutApplicationSettingsTab.ASSISTED) {
              setAssistedSelected(true);
              setValues(assistedTabData, true);
            } else {
              setAssistedSelected(false);
              setValues(manualTabData, true);
            }

            // TODO when switching tabs between assisted and manual, sometimes validation doesn't take
            // it appears there's a certain delay that does work ~2 seconds manually
            // this is hacky, but will enforce a validation
            (async () => {
              await sleep(1000);
              validateForm();
            })();
          }}
        />
      );
    },
    [assistedTabData, initialTab, manualTabData, tabs]
  );

  const onValuesChanged = useCallback(
    (values: any) => {
      if (assistedSelected) {
        setAssistedTabData(values);
        setManualTabData({ data: manualTabData.data, tags: values.tags });
      } else {
        setManualTabData(values);
        setAssistedTabData({
          data: assistedTabData.data,
          tags: values.tags,
        });
      }
    },
    [assistedSelected, assistedTabData.data, manualTabData.data]
  );

  const innerOnSubmit = useCallback(
    (form: T) => {
      if (assistedSelected) {
        onSubmit(form, recommendationId);
      } else {
        // This will force the recommendation to be removed if saving in the manual tab
        onSubmit(form);
      }
    },
    [assistedSelected, onSubmit, recommendationId]
  );

  return (
    <Formik
      initialValues={formValues}
      onSubmit={innerOnSubmit}
      enableReinitialize={true}
      validationSchema={validationSchema}
      innerRef={setInnerRef}
    >
      {(formikProps) => {
        const internalPreForm =
          typeof preForm === 'function' ? preForm({ formikProps }) : null;
        const internalPostForm =
          typeof postForm === 'function' ? postForm({ formikProps }) : null;
        const internalFooter = footer({
          formikProps,
          disabled: recommendationLoading,
        });

        return (
          <InnerForm
            formikProps={formikProps}
            onValuesChanged={onValuesChanged}
            excludeTags={excludeTags}
            footer={internalFooter}
            postForm={internalPostForm}
            preForm={internalPreForm}
            tagsReadOnly={tagsReadOnly}
            topTabs={topTabs}
          />
        );
      }}
    </Formik>
  );
}

interface InnerFormProps<T> {
  excludeTags: string[] | undefined;
  footer: ReactElement;
  formikProps: FormikProps<T>;
  onValuesChanged: (values: unknown) => void;
  postForm?: ReactElement | null;
  preForm?: ReactElement | null;
  tagsReadOnly?: boolean;
  topTabs: (
    setValues: (values: T, validate?: boolean) => void,
    validateForm: () => void
  ) => ReactElement;
}

const InnerForm = <
  T extends Pick<
    ApplicationDefaultParameterSetFormikFormValues,
    'tags' | 'data'
  >,
>(
  props: InnerFormProps<T>
) => {
  const {
    excludeTags,
    footer,
    formikProps,
    onValuesChanged,
    postForm,
    preForm,
    tagsReadOnly,
    topTabs,
  } = props;
  const { values, setValues, validateForm } = formikProps;

  const isMounted = useIsMounted();

  useEffect(() => {
    if (!isMounted()) {
      return;
    }

    onValuesChanged(values);
  }, [isMounted, onValuesChanged, values]);

  const process = getParameterValueFromForm<BeaconCuttingData.CutProcessValue>(
    values.data.parameters,
    BeaconCuttingData.FlowItemParameterRef.PROCESS
  );

  return (
    <ContentWithFooter footer={footer} forceHeightForFooter={true}>
      <Form>
        {preForm}
        <CutApplicationSettingsTags
          process={process}
          parameterSetFormTags={values.tags}
          readOnly={tagsReadOnly}
          excludeTags={excludeTags}
        />
        <div style={{ height: 20 }} />
        {topTabs(setValues, validateForm)}
        {postForm}
      </Form>
    </ContentWithFooter>
  );
};

export { CutApplicationSettingsBaseFormTabbed };
